/**
 * Toast store — Zustand slice for the toast stack.
 *
 * Phase 1 (post-walkthrough):
 * - Added `action?: { label: string; onClick: () => void }` slot.
 * - REMOVED the hardcoded 5s auto-dismiss `setTimeout` from `addToast`.
 *   Timer is now owned by the `<Toast>` component via `useEffect` so
 *   per-variant timing (5s/8s/never) and hover-pause work correctly.
 * - `addToast(msg, variant?)` signature preserved for ~14 unchanged call
 *   sites (positional back-compat).
 *
 * Spec: design/post-walkthrough/global-patterns-spec.md §1.2.
 */
import { create } from "zustand";

export type ToastVariant = "success" | "error" | "info" | "warning";

export type ToastAction = {
  label: string;
  onClick: () => void;
};

export type Toast = {
  id: string;
  message: string;
  variant: ToastVariant;
  /** Optional inline action button (e.g. "Retry", "Undo"). */
  action?: ToastAction;
};

type ToastState = {
  toasts: Toast[];
  addToast: (message: string, variant?: ToastVariant, action?: ToastAction) => void;
  removeToast: (id: string) => void;
};

/** Max simultaneous toasts in the stack. Oldest dismissed on overflow. */
const MAX_TOASTS = 3;

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  addToast: (message, variant = "info", action) => {
    const id = crypto.randomUUID();
    set((state) => {
      const next = [...state.toasts, { id, message, variant, action }];
      // Trim oldest from the front when over the cap. The dismissed
      // entries had their component-level timers orphaned; that's
      // acceptable — removeToast is idempotent on missing IDs.
      return { toasts: next.slice(-MAX_TOASTS) };
    });
    // NOTE: Auto-dismiss timer intentionally NOT here.
    // The <Toast> component manages per-variant timing via useEffect
    // (5s success/info, 8s warning, never for error).
  },

  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((t) => t.id !== id),
    }));
  },
}));
